import { View } from 'react-native';
import { styles } from './DiscountTable.style';
import { useStore } from 'src/contexts/StoreContext';
import { Icon } from '../icon/Icon';
import { ICON_NAMES } from '../icon/icon.helper';
import { brand, progressColor } from 'src/theme/colors';
import Animated from 'react-native-reanimated';
import { DiscountScale } from '@photosi/expo-photosi-sdk/build/Project';
import { useDiscountInfo } from './useDiscountInfo';
import { useDiscountTableLogic } from './DiscountTable.logic';
import TextLato from '../text/text-lato/TextLato';
import { makeBold } from 'src/helpers/text.helper';
import { DiscountScaleProgressBig } from '../discount-scale-progress/discount-scale-progress-big/DiscountScaleProgressBig';

export const DiscountTable = ({
  quantity,
  discount,
  isMobile,
  withBackground = true,
  showAnimationMilestone = true,
  v3 = false,
}: {
  quantity: number;
  discount: DiscountScale[];
  isMobile?: boolean;
  withBackground?: boolean;
  showAnimationMilestone?: boolean;
  v3?: boolean;
}) => {
  const { i18n } = useStore();
  const { progress, discountValue, nextDiscount, missingForNext } = useDiscountInfo(quantity, discount);

  const { titleTextAnimatedStyle, titleTextReachedAnimatedStyle, anyMilestoneShowingCheckSV, isSmallDesktop } =
    useDiscountTableLogic(discount, progress, isMobile);

  return !isMobile ? (
    <View
      style={[
        styles.container,
        withBackground && { backgroundColor: progressColor.background },
        !v3 && { padding: 24 },
      ]}
    >
      {!isSmallDesktop && (
        <View style={styles.left}>
          <View style={styles.iconPromo}>
            <Icon name={ICON_NAMES.Promo} color={brand.main.orange} />
          </View>
          <View style={styles.leftInner}>
            <Animated.View style={[titleTextAnimatedStyle, styles.textWrapper]}>
              <TextLato style={styles.title}>
                {discountValue != '0'
                  ? i18n.t('discounts-table:prints-description', { discount: discountValue })
                  : i18n.t('discounts-table:prints-title')}
              </TextLato>
              {nextDiscount && missingForNext && missingForNext > 0 && (
                <TextLato style={styles.subtitle}>
                  {i18n.t('discounts-table:prints-to-reach-head')}
                  {makeBold(i18n.t('discounts-table:prints-to-reach-middle', { count: missingForNext }))}
                  {i18n.t('discounts-table:prints-to-reach-tail', { discount: makeBold(`${nextDiscount}%`) })}
                </TextLato>
              )}
            </Animated.View>
            <Animated.View style={[titleTextReachedAnimatedStyle, styles.textWrapper]}>
              <TextLato style={styles.title}>
                {i18n.t('discounts-table:prints-description', { discount: discountValue })}
              </TextLato>
              <TextLato style={styles.subtitle}>{i18n.t('discounts-table:greetings')}</TextLato>
            </Animated.View>
          </View>
        </View>
      )}

      <View style={v3 ? styles.rightV3 : styles.right}>
        <DiscountScaleProgressBig
          discountScales={discount}
          quantity={quantity}
          containerStyle={styles.progressWrapper}
          anyMilestoneShowingCheckSV={anyMilestoneShowingCheckSV}
          showAnimationMilestone={showAnimationMilestone}
          isMobile={isMobile}
        />
      </View>
    </View>
  ) : (
    <View style={styles.containerMobile}>
      <Animated.View style={[titleTextAnimatedStyle, styles.textWrapperMobile]}>
        <TextLato style={styles.title}>
          {discountValue != '0'
            ? i18n.t('discounts-table:prints-description', { discount: discountValue })
            : i18n.t('discounts-table:prints-title')}
        </TextLato>
        {nextDiscount && missingForNext && missingForNext > 0 && (
          <TextLato style={styles.subtitle}>
            {i18n.t('discounts-table:prints-to-reach-head')}
            {makeBold(i18n.t('discounts-table:prints-to-reach-middle', { count: missingForNext }))}
            {i18n.t('discounts-table:prints-to-reach-tail', { discount: makeBold(`${nextDiscount}%`) })}
          </TextLato>
        )}
      </Animated.View>
      <Animated.View style={[titleTextReachedAnimatedStyle, styles.textWrapperMobile]}>
        <TextLato style={styles.title}>
          {i18n.t('discounts-table:prints-description', { discount: discountValue })}
        </TextLato>
        <TextLato style={styles.subtitle}>{i18n.t('discounts-table:greetings')}</TextLato>
      </Animated.View>
      <DiscountScaleProgressBig
        discountScales={discount}
        quantity={quantity}
        containerStyle={styles.progressWrapperMobile}
        anyMilestoneShowingCheckSV={anyMilestoneShowingCheckSV}
        showAnimationMilestone={showAnimationMilestone}
        isMobile={isMobile}
      />
    </View>
  );
};
